import React from 'react'

import { Box, Radio, Typography } from '@mui/material'

import styles from './RadioCardGroup.styles'
import type { RadioCardProps } from './RadioCardGroup.types'

/**
 * Single selectable card with radio styling (title, optional description, optional input slot).
 * Used by RadioCardGroup for vertical ZIP/option selection.
 */
const RadioCard: React.FC<RadioCardProps> = ({
  name,
  value,
  title,
  description,
  selected,
  disabled,
  onSelect,
  children
}) => {
  const handleClick = () => {
    if (!disabled) onSelect()
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (!disabled) onSelect()
    }
  }

  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? undefined : 0}
      sx={{ ...styles.card, ...(selected ? styles.cardSelected : {}), ...(disabled ? styles.cardDisabled : {}) }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <Radio
        name={name}
        value={value}
        checked={selected}
        disabled={disabled}
        sx={styles.hiddenRadio}
        tabIndex={-1}
        aria-hidden
      />
      <Box
        sx={{
          ...styles.radioOuter,
          ...(selected ? styles.radioOuterSelected : {}),
          ...(disabled ? styles.radioOuterDisabled : {})
        }}
      >
        {selected && <Box sx={styles.radioDot} />}
      </Box>
      <Box sx={styles.textBlock}>
        <Typography component="span" sx={{ ...styles.title, ...(disabled ? styles.titleDisabled : {}) }}>
          {title}
        </Typography>
        {description != null && description !== '' && (
          <Typography component="span" sx={{ ...styles.description, ...(disabled ? styles.descriptionDisabled : {}) }}>
            {description}
          </Typography>
        )}
        {children != null && <Box sx={styles.inputSlot}>{children}</Box>}
      </Box>
    </Box>
  )
}

export default RadioCard
